/**
 * NUEVO SISTEMA DE TRADUCCIÓN ROBUSTO CON NAMESPACES
 * Soluciona los problemas de conflictos de claves de manera definitiva
 */

import { Language, LanguageText } from './languageData';

// Definición del nuevo sistema de traducción organizado por namespaces
export interface TranslationNamespaces {
  // Navegación y header
  nav: {
    home: LanguageText;
    findTherapist: LanguageText;
    specialties: LanguageText;
    howItWorks: LanguageText;
    forTherapists: LanguageText;
    pricing: LanguageText;
    about: LanguageText;
    login: LanguageText;
    signup: LanguageText;
    menu: LanguageText;
    myAccount: LanguageText;
    signOut: LanguageText;
  };
  
  // Contenido de la página principal
  home: {
    heroTitle: LanguageText;
    heroDescription: LanguageText;
    findPerfectMatch: LanguageText;
    getStarted: LanguageText;
    joinAsProfessional: LanguageText;
    verifiedProfessionals: LanguageText;
    securePrivate: LanguageText;
    flexibleSchedules: LanguageText;
    whyChooseUs: LanguageText;
    trustedByMillions: LanguageText;
  };
  
  // Sección "Cómo funciona"
  howItWorks: {
    title: LanguageText;
    description: LanguageText;
    step1Title: LanguageText;
    step1Description: LanguageText;
    step2Title: LanguageText;
    step2Description: LanguageText;
    step3Title: LanguageText;
    step3Description: LanguageText;
    step4Title: LanguageText;
    step4Description: LanguageText;
  };
  
  // Filtros y búsqueda
  filters: {
    any: LanguageText;
    online: LanguageText;
    inPerson: LanguageText;
    language: LanguageText;
    priceRange: LanguageText;
    therapistType: LanguageText;
  };
  
  // Elementos comunes
  common: {
    countries: LanguageText;
    cities: LanguageText;
    and: LanguageText;
    verified: LanguageText;
    secure: LanguageText;
    flexible: LanguageText;
  };
}

// Definición completa de las traducciones organizadas por namespaces
const translations: TranslationNamespaces = {
  nav: {
    home: {
      en: 'Home',
      es: 'Inicio', 
      fr: 'Accueil',
      de: 'Startseite',
      it: 'Home',
      pt: 'Início'
    },
    findTherapist: {
      en: 'Find Psychotherapists',
      es: 'Encontrar Psicoterapeutas',
      fr: 'Trouver des Psychothérapeutes', 
      de: 'Psychotherapeuten Finden',
      it: 'Trova Psicoterapeuti',
      pt: 'Encontrar Psicoterapeutas'
    },
    specialties: {
      en: 'Specialties',
      es: 'Especialidades',
      fr: 'Spécialités',
      de: 'Fachgebiete',
      it: 'Specialità',
      pt: 'Especialidades'
    },
    howItWorks: {
      en: 'How It Works',
      es: 'Cómo Funciona',
      fr: 'Comment Ça Marche',
      de: 'Wie Es Funktioniert',
      it: 'Come Funziona', 
      pt: 'Como Funciona'
    },
    forTherapists: {
      en: 'For Psychotherapists',
      es: 'Para Psicoterapeutas',
      fr: 'Pour les Psychothérapeutes',
      de: 'Für Psychotherapeuten',
      it: 'Per Psicoterapeuti',
      pt: 'Para Psicoterapeutas'
    },
    pricing: {
      en: 'Pricing',
      es: 'Precios',
      fr: 'Tarifs',
      de: 'Preise',
      it: 'Prezzi',
      pt: 'Preços'
    },
    about: {
      en: 'About',
      es: 'Acerca de',
      fr: 'À propos',
      de: 'Über',
      it: 'Informazioni',
      pt: 'Sobre'
    },
    login: {
      en: 'Log in',
      es: 'Iniciar sesión',
      fr: 'Se connecter',
      de: 'Anmelden',
      it: 'Accedi',
      pt: 'Entrar'
    },
    signup: {
      en: 'Sign up',
      es: 'Registrarse',
      fr: 'S\'inscrire',
      de: 'Registrieren',
      it: 'Registrati',
      pt: 'Cadastrar-se'
    },
    menu: {
      en: 'Menu',
      es: 'Menú',
      fr: 'Menu',
      de: 'Menü',
      it: 'Menu',
      pt: 'Menu'
    },
    myAccount: {
      en: 'My Account',
      es: 'Mi Cuenta',
      fr: 'Mon Compte',
      de: 'Mein Konto',
      it: 'Il Mio Account',
      pt: 'Minha Conta'
    },
    signOut: {
      en: 'Sign Out',
      es: 'Cerrar Sesión',
      fr: 'Se Déconnecter',
      de: 'Abmelden',
      it: 'Disconnetti',
      pt: 'Sair'
    }
  },
  
  home: {
    heroTitle: {
      en: 'Find Your Perfect Psychotherapist',
      es: 'Encuentra Tu Psicoterapeuta Ideal',
      fr: 'Trouvez Votre Psychothérapeute Idéal',
      de: 'Finden Sie Ihren Perfekten Psychotherapeuten',
      it: 'Trova il Tuo Psicoterapeuta Perfetto',
      pt: 'Encontre Seu Psicoterapeuta Perfeito'
    },
    heroDescription: {
      en: 'Connect with certified psychologists, psychiatrists and psychotherapists worldwide. Find professional mental health support in over 150 countries and 1,000+ cities.',
      es: 'Conéctate con psicólogos, psiquiatras y psicoterapeutas certificados en todo el mundo. Encuentra apoyo profesional de salud mental en más de 150 países y 1,000+ ciudades.',
      fr: 'Connectez-vous avec des psychologues, psychiatres et psychothérapeutes certifiés dans le monde entier. Trouvez un soutien professionnel en santé mentale dans plus de 150 pays et 1,000+ villes.',
      de: 'Verbinden Sie sich mit zertifizierten Psychologen, Psychiatern und Psychotherapeuten weltweit. Finden Sie professionelle Unterstützung für die psychische Gesundheit in über 150 Ländern und 1,000+ Städten.',
      it: 'Connettiti con psicologi, psichiatri e psicoterapeuti certificati in tutto il mondo. Trova supporto professionale per la salute mentale in oltre 150 paesi e 1,000+ città.',
      pt: 'Conecte-se com psicólogos, psiquiatras e psicoterapeutas certificados em todo o mundo. Encontre suporte profissional de saúde mental em mais de 150 países e 1,000+ cidades.'
    },
    findPerfectMatch: {
      en: 'Find Your Perfect Match',
      es: 'Encuentra Tu Coincidencia Perfecta',
      fr: 'Trouvez Votre Correspondance Parfaite',
      de: 'Finden Sie Ihre Perfekte Übereinstimmung',
      it: 'Trova la Tua Corrispondenza Perfetta',
      pt: 'Encontre Sua Correspondência Perfeita'
    },
    getStarted: {
      en: 'Get Started',
      es: 'Comenzar',
      fr: 'Commencer',
      de: 'Loslegen',
      it: 'Inizia',
      pt: 'Começar'
    },
    joinAsProfessional: {
      en: 'Join as Professional',
      es: 'Únete como Profesional',
      fr: 'Rejoindre en tant que Professionnel',
      de: 'Als Fachkraft beitreten',
      it: 'Iscriviti come Professionista',
      pt: 'Junte-se como Profissional'
    },
    verifiedProfessionals: {
      en: 'Verified Professionals',
      es: 'Profesionales Verificados',
      fr: 'Professionnels Vérifiés',
      de: 'Verifizierte Fachkräfte',
      it: 'Professionisti Verificati',
      pt: 'Profissionais Verificados'
    },
    securePrivate: {
      en: 'Secure & Private',
      es: 'Seguro y Privado',
      fr: 'Sécurisé et Privé',
      de: 'Sicher und Privat',
      it: 'Sicuro e Privato',
      pt: 'Seguro e Privado'
    },
    flexibleSchedules: {
      en: 'Flexible Scheduling',
      es: 'Programación Flexible',
      fr: 'Planification Flexible',
      de: 'Flexible Terminplanung',
      it: 'Programmazione Flessibile',
      pt: 'Agendamento Flexível'
    },
    whyChooseUs: {
      en: 'Why Choose Our Platform?',
      es: '¿Por Qué Elegir Nuestra Plataforma?',
      fr: 'Pourquoi Choisir Notre Plateforme?',
      de: 'Warum Unsere Plattform Wählen?',
      it: 'Perché Scegliere la Nostra Piattaforma?',
      pt: 'Por Que Escolher Nossa Plataforma?'
    },
    trustedByMillions: {
      en: 'Trusted by millions worldwide for professional, secure, and accessible mental health care',
      es: 'Confiado por millones en todo el mundo para atención de salud mental profesional, segura y accesible',
      fr: 'Approuvé par des millions dans le monde entier pour des soins de santé mentale professionnels, sécurisés et accessibles',
      de: 'Von Millionen weltweit vertraut für professionelle, sichere und zugängliche psychische Gesundheitsversorgung',
      it: 'Affidabile da milioni in tutto il mondo per cure della salute mentale professionali, sicure e accessibili',
      pt: 'Confiável por milhões em todo o mundo para cuidados de saúde mental profissionais, seguros e acessíveis'
    }
  },
  
  howItWorks: {
    title: {
      en: 'How It Works',
      es: 'Cómo Funciona',
      fr: 'Comment Ça Marche',
      de: 'Wie Es Funktioniert',
      it: 'Come Funziona',
      pt: 'Como Funciona'
    },
    description: {
      en: 'Simple steps to connect with your ideal mental health professional',
      es: 'Pasos simples para conectarte con tu profesional de salud mental ideal',
      fr: 'Étapes simples pour vous connecter avec votre professionnel de santé mentale idéal',
      de: 'Einfache Schritte, um sich mit Ihrem idealen Fachmann für psychische Gesundheit zu verbinden',
      it: 'Semplici passi per connetterti con il tuo professionista della salute mentale ideale',
      pt: 'Passos simples para se conectar com seu profissional de saúde mental ideal'
    },
    step1Title: {
      en: 'Find Your Therapist',
      es: 'Encuentra Tu Terapeuta',
      fr: 'Trouvez Votre Thérapeute',
      de: 'Finden Sie Ihren Therapeuten',
      it: 'Trova il Tuo Terapeuta',
      pt: 'Encontre Seu Terapeuta'
    },
    step1Description: {
      en: 'Search by location, specialty, language, and availability to find the perfect therapist for your needs.',
      es: 'Busca por ubicación, especialidad, idioma y disponibilidad para encontrar el terapeuta perfecto para tus necesidades.',
      fr: 'Recherchez par lieu, spécialité, langue et disponibilité pour trouver le thérapeute parfait pour vos besoins.',
      de: 'Suchen Sie nach Standort, Spezialität, Sprache und Verfügbarkeit, um den perfekten Therapeuten für Ihre Bedürfnisse zu finden.',
      it: 'Cerca per posizione, specialità, lingua e disponibilità per trovare il terapeuta perfetto per le tue esigenze.',
      pt: 'Pesquise por localização, especialidade, idioma e disponibilidade para encontrar o terapeuta perfeito para suas necessidades.'
    },
    step2Title: {
      en: 'Book Your Session',
      es: 'Reserva Tu Sesión',
      fr: 'Réservez Votre Séance',
      de: 'Buchen Sie Ihre Sitzung',
      it: 'Prenota la Tua Sessione',
      pt: 'Agende Sua Sessão'
    },
    step2Description: {
      en: 'Select a convenient time that fits your schedule and complete secure payment processing.',
      es: 'Selecciona un horario conveniente que se adapte a tu horario y completa el procesamiento de pago seguro.',
      fr: 'Sélectionnez un moment convenable qui correspond à votre emploi du temps et complétez le traitement de paiement sécurisé.',
      de: 'Wählen Sie eine bequeme Zeit, die zu Ihrem Zeitplan passt, und schließen Sie die sichere Zahlungsabwicklung ab.',
      it: 'Seleziona un orario conveniente che si adatta al tuo programma e completa l\'elaborazione del pagamento sicuro.',
      pt: 'Selecione um horário conveniente que se adapte à sua agenda e complete o processamento de pagamento seguro.'
    },
    step3Title: {
      en: 'Connect Securely',
      es: 'Conecta de Forma Segura',
      fr: 'Connectez-vous en Sécurité',
      de: 'Sicher Verbinden',
      it: 'Connettiti in Sicurezza',
      pt: 'Conecte-se com Segurança'
    },
    step3Description: {
      en: 'Join your session through our secure video platform or meet in person at the designated location.',
      es: 'Únete a tu sesión a través de nuestra plataforma de video segura o reúnete en persona en el lugar designado.',
      fr: 'Rejoignez votre séance via notre plateforme vidéo sécurisée ou rencontrez-vous en personne au lieu désigné.',
      de: 'Nehmen Sie an Ihrer Sitzung über unsere sichere Video-Plattform teil oder treffen Sie sich persönlich am vorgesehenen Ort.',
      it: 'Unisciti alla tua sessione attraverso la nostra piattaforma video sicura o incontrati di persona nel luogo designato.',
      pt: 'Participe da sua sessão através de nossa plataforma de vídeo segura ou encontre-se pessoalmente no local designado.'
    },
    step4Title: {
      en: 'Continue Your Journey',
      es: 'Continúa Tu Viaje',
      fr: 'Continuez Votre Voyage',
      de: 'Setzen Sie Ihre Reise Fort',
      it: 'Continua il Tuo Viaggio',
      pt: 'Continue Sua Jornada'
    },
    step4Description: {
      en: 'Schedule follow-up sessions and build a lasting therapeutic relationship for ongoing support.',
      es: 'Programa sesiones de seguimiento y construye una relación terapéutica duradera para apoyo continuo.',
      fr: 'Planifiez des séances de suivi et construisez une relation thérapeutique durable pour un soutien continu.',
      de: 'Planen Sie Nachfolgesitzungen und bauen Sie eine dauerhafte therapeutische Beziehung für kontinuierliche Unterstützung auf.',
      it: 'Programma sessioni di follow-up e costruisci una relazione terapeutica duratura per supporto continuo.',
      pt: 'Agende sessões de acompanhamento e construa um relacionamento terapêutico duradouro para suporte contínuo.'
    }
  },
  
  filters: {
    any: {
      en: 'Any',
      es: 'Cualquiera',
      fr: 'N\'importe',
      de: 'Beliebig',
      it: 'Qualsiasi',
      pt: 'Qualquer'
    },
    online: {
      en: 'Online',
      es: 'En Línea',
      fr: 'En Ligne',
      de: 'Online',
      it: 'Online',
      pt: 'Online'
    },
    inPerson: {
      en: 'In Person',
      es: 'Presencial',
      fr: 'En Personne',
      de: 'Persönlich',
      it: 'Di Persona',
      pt: 'Presencial'
    },
    language: {
      en: 'Language',
      es: 'Idioma',
      fr: 'Langue',
      de: 'Sprache',
      it: 'Lingua',
      pt: 'Idioma'
    },
    priceRange: {
      en: 'Price Range',
      es: 'Rango de Precio',
      fr: 'Gamme de Prix',
      de: 'Preisklasse',
      it: 'Fascia di Prezzo',
      pt: 'Faixa de Preço'
    },
    therapistType: {
      en: 'Therapist Type',
      es: 'Tipo de Terapeuta',
      fr: 'Type de Thérapeute',
      de: 'Therapeutentyp',
      it: 'Tipo di Terapeuta',
      pt: 'Tipo de Terapeuta'
    }
  },
  
  common: {
    countries: {
      en: 'countries',
      es: 'países',
      fr: 'pays',
      de: 'länder',
      it: 'paesi',
      pt: 'países'
    },
    cities: {
      en: 'cities',
      es: 'ciudades',
      fr: 'villes',
      de: 'städte',
      it: 'città',
      pt: 'cidades'
    },
    and: {
      en: 'and',
      es: 'y',
      fr: 'et',
      de: 'und',
      it: 'e',
      pt: 'e'
    },
    verified: {
      en: 'Verified',
      es: 'Verificado',
      fr: 'Vérifié',
      de: 'Verifiziert',
      it: 'Verificato',
      pt: 'Verificado'
    },
    secure: {
      en: 'Secure',
      es: 'Seguro',
      fr: 'Sécurisé',
      de: 'Sicher',
      it: 'Sicuro',
      pt: 'Seguro'
    },
    flexible: {
      en: 'Flexible',
      es: 'Flexible',
      fr: 'Flexible',
      de: 'Flexibel',
      it: 'Flessibile',
      pt: 'Flexível'
    }
  }
};

export default translations;

// Función utilitaria para acceder a las traducciones de forma segura
export const getTranslation = (namespace: keyof TranslationNamespaces, key: string, language: Language): string => {
  try {
    const namespaceObj = translations[namespace];
    if (!namespaceObj) {
      console.warn(`Namespace not found: ${namespace}`);
      return formatFallbackText(key);
    }
    
    const translationObj = (namespaceObj as any)[key];
    if (!translationObj) {
      console.warn(`Translation key not found: ${namespace}.${key}`);
      return formatFallbackText(key);
    }
    
    // Intentar obtener la traducción en el idioma solicitado
    if (translationObj[language]) {
      return translationObj[language];
    }
    
    // Fallback a inglés
    if (translationObj['en']) {
      return translationObj['en'];
    }
    
    // Último recurso: formatear el texto legible
    return formatFallbackText(key);
    
  } catch (error) {
    console.error(`Error getting translation for ${namespace}.${key}:`, error);
    return formatFallbackText(key);
  }
};

// Función para formatear texto legible como fallback
const formatFallbackText = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};